/**
 * Cho'ktiruvchi quvurli sentrifuga — Σ-nazariya (Ambler), Stokes cho'kishi.
 *
 * TENGLAMALAR
 *   ω = 2πn/60;  K = ω²R/g
 *   Σ = π·ω²·L·(R² − r₀²) / (g·ln(2R²/(R² + r₀²)))         — quvurli baraban (Ambler)
 *   v_g(d) = (ρ_p − ρ)·g·d² / (18 μ)                          — Stokes, gravitatsiya ostida
 *   Unumdorlik: Q = 2·v_g(d_c)·Σ  →  d_c = √(18 μ Q / (2 Σ g (ρ_p − ρ)))
 *   Ushlanish darajasi (tekis kirish taqsimoti): G(d) = min(1, (d/d_c)²)
 *   Umumiy η = ∫ G(d) f_m(d) dd,  f_m — log-normal massa taqsimoti (d_50, σ_g)
 *   Cho'kma: V_c = m/(ρ_p(1 − ε)) devorda halqa → R_eff² = R² − V_c/(πL); Σ R_eff bilan
 *   Quvvat: N = [Q·ρ·ω²·R²/2 + k_w·ω³] / η_yuritma   (suyuqlikka kinetik energiya + havo)
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR
 *   1. Laminar (Stokes) cho'kish, zarrachalar sferik, o'zaro ta'sirsiz (c kichik).
 *   2. Suyuqlik barabanda bo'ylama tekis oqadi (siljishsiz), kirishda zarrachalar tekis taqsimlangan.
 *   3. Cho'kma siqilmaydi, g'ovaklik doimiy; tushirish oniy.
 *   4. Harorat μ ga ta'sir qiladi, ρ_p doimiy.
 *
 * NOMUVOFIQ REJIMLAR: n > n_max — ruxsat etilmagan tezlik (critical, n_max ga cheklanadi);
 * baraban cho'kma bilan to'lgan — Σ = 0, hamma qattiq faza o'tib ketadi (critical);
 * η < 0.5 — past samara (warning); Re_p > 1 — Stokes chegarasidan tashqari (info).
 *
 * MANBA: Ambler (1952); Perry's 8th ed., 18-bob (Centrifuges, Σ theory); Kasatkin, 6-bob;
 * Svarovsky, "Solid-Liquid Separation", 7-bob.
 */
import { liquidDensity, viscosity } from '../../properties';
import { warn, type SteadyResult, type Warning } from '../../types';
import type { CentrifugeInputs, CentrifugeOutputs, CentrifugeParams } from './types';

export const G = 9.80665;
export const DEFAULT_TEMPERATURE = 293.15;
export const DEFAULT_SIGMA_G = 1.6;
export const DEFAULT_POROSITY = 0.5;
export const DEFAULT_DRIVE_EFF = 0.85;

export function validateCentrifugeParams(p: CentrifugeParams): void {
  if (!(p.bowlRadius > 0)) throw new Error('centrifuge: bowlRadius must be > 0');
  if (!(p.innerRadius >= 0 && p.innerRadius < p.bowlRadius)) throw new Error('centrifuge: innerRadius must be in [0, bowlRadius)');
  if (!(p.bowlLength > 0)) throw new Error('centrifuge: bowlLength must be > 0');
  if (!(p.maxSpeed > 0)) throw new Error('centrifuge: maxSpeed must be > 0');
  if (!(p.particleDensity > 0)) throw new Error('centrifuge: particleDensity must be > 0');
  if (!(p.particleD50 > 0)) throw new Error('centrifuge: particleD50 must be > 0');
  if (p.particleSigma !== undefined && !(p.particleSigma > 1)) throw new Error('centrifuge: particleSigma must be > 1');
  if (!p.liquid && !(p.liquidDensity !== undefined && p.liquidDensity > 0 && p.viscosity !== undefined && p.viscosity > 0))
    throw new Error('centrifuge: liquid or (liquidDensity, viscosity) required');
  if (!(p.solidsHoldup > 0)) throw new Error('centrifuge: solidsHoldup must be > 0');
}

export function validateCentrifugeInputs(i: CentrifugeInputs): void {
  if (!(i.flowRate >= 0)) throw new Error('centrifuge: flowRate must be ≥ 0');
  if (!(i.speed >= 0)) throw new Error('centrifuge: speed must be ≥ 0');
  if (!(i.solidsConcentration >= 0)) throw new Error('centrifuge: solidsConcentration must be ≥ 0');
}

export function fluidProps(p: CentrifugeParams, T: number, warnings?: Warning[]): { rho: number; mu: number } {
  if (p.liquid) return { rho: liquidDensity(p.liquid, T, warnings), mu: viscosity(p.liquid, 'liquid', T, warnings) };
  return { rho: p.liquidDensity as number, mu: p.viscosity as number };
}

/** Σ (m²) — samarali radius R_eff bilan; R_eff ≤ r₀ bo'lsa 0 */
export function sigmaFactor(p: CentrifugeParams, omega: number, Reff: number): number {
  const r0 = p.innerRadius;
  if (Reff <= r0 + 1e-12 || omega <= 0) return 0;
  const num = Math.PI * omega * omega * p.bowlLength * (Reff * Reff - r0 * r0);
  const den = G * Math.log((2 * Reff * Reff) / (Reff * Reff + r0 * r0));
  return num / den;
}

/** Cho'kma massasi bo'yicha samarali baraban radiusi */
export function effectiveRadius(p: CentrifugeParams, solidsMass: number): number {
  const eps = p.cakePorosity ?? DEFAULT_POROSITY;
  const Vc = solidsMass / (p.particleDensity * (1 - eps));
  const R2 = p.bowlRadius * p.bowlRadius - Vc / (Math.PI * p.bowlLength);
  return R2 > 0 ? Math.sqrt(R2) : 0;
}

/** Stokes cho'kish tezligi gravitatsiya ostida, m/s */
export function stokesVelocity(d: number, rhoP: number, rho: number, mu: number): number {
  return ((rhoP - rho) * G * d * d) / (18 * mu);
}

/** Log-normal massa taqsimoti bo'yicha umumiy ushlanish darajasi (Gauss–Ermit o'rniga tekis panjara, 400 nuqta) */
export function overallRecovery(dCut: number, d50: number, sigmaG: number): number {
  if (!(dCut > 0)) return 1;
  const s = Math.log(sigmaG);
  const n = 400;
  const lo = Math.log(d50) - 5 * s;
  const hi = Math.log(d50) + 5 * s;
  const h = (hi - lo) / n;
  let sum = 0;
  for (let k = 0; k <= n; k++) {
    const y = lo + k * h;
    const d = Math.exp(y);
    const f = Math.exp(-((y - Math.log(d50)) ** 2) / (2 * s * s)) / (s * Math.sqrt(2 * Math.PI));
    const Gd = Math.min(1, (d / dCut) ** 2);
    const w = k === 0 || k === n ? 0.5 : 1;
    sum += w * Gd * f;
  }
  return Math.min(1, sum * h);
}

export interface CentrifugePoint {
  omega: number;
  separationFactor: number;
  sigma: number;
  dCut: number;
  recovery: number;
  power: number;
  effectiveRadius: number;
  reParticle: number;
  rho: number;
  mu: number;
}

/** Berilgan holat (cho'kma massasi) uchun oniy ko'rsatkichlar */
export function evaluate(p: CentrifugeParams, i: CentrifugeInputs, solidsMass: number, warnings: Warning[]): CentrifugePoint {
  const T = i.temperature ?? DEFAULT_TEMPERATURE;
  const { rho, mu } = fluidProps(p, T, warnings);
  let n = i.speed;
  if (n > p.maxSpeed) {
    warn(warnings, 'cent.overspeed', 'critical', { speed: Math.round(n), maxSpeed: p.maxSpeed });
    n = p.maxSpeed;
  }
  const omega = (2 * Math.PI * n) / 60;
  const Reff = effectiveRadius(p, solidsMass);
  const sigma = sigmaFactor(p, omega, Reff);
  const dRho = p.particleDensity - rho;
  let dCut: number;
  if (sigma <= 0 || dRho <= 0) dCut = Infinity;
  else if (i.flowRate <= 0) dCut = 0;
  else dCut = Math.sqrt((18 * mu * i.flowRate) / (2 * sigma * G * dRho));
  const recovery = Number.isFinite(dCut) ? overallRecovery(dCut, p.particleD50, p.particleSigma ?? DEFAULT_SIGMA_G) : 0;
  const eff = p.driveEfficiency ?? DEFAULT_DRIVE_EFF;
  const power = (0.5 * i.flowRate * rho * omega * omega * p.bowlRadius * p.bowlRadius + (p.windage ?? 0) * omega ** 3) / eff;
  const vC = Number.isFinite(dCut) && dCut > 0 ? stokesVelocity(dCut, p.particleDensity, rho, mu) * ((omega * omega * p.bowlRadius) / G) : 0;
  const reParticle = Number.isFinite(dCut) ? (rho * vC * dCut) / mu : 0;
  return { omega, separationFactor: (omega * omega * p.bowlRadius) / G, sigma, dCut, recovery, power, effectiveRadius: Reff, reParticle, rho, mu };
}

export function solveSteady(p: CentrifugeParams, i: CentrifugeInputs): SteadyResult<CentrifugeOutputs> {
  validateCentrifugeParams(p);
  validateCentrifugeInputs(i);
  const warnings: Warning[] = [];
  const e = evaluate(p, i, 0, warnings);
  const captured = i.flowRate * i.solidsConcentration * e.recovery;
  if (e.sigma <= 0) warn(warnings, 'cent.noSeparation', 'critical');
  else if (e.recovery < 0.5) warn(warnings, 'cent.lowRecovery', 'warning', { recovery: Math.round(e.recovery * 100) });
  if (e.reParticle > 1) warn(warnings, 'cent.stokesInvalid', 'info', { re: Math.round(e.reParticle * 10) / 10 });
  const outputs: CentrifugeOutputs = {
    t: 0,
    omega: e.omega,
    separationFactor: e.separationFactor,
    sigma: e.sigma,
    dCut: e.dCut,
    recovery: e.recovery,
    overflowSolids: i.solidsConcentration * (1 - e.recovery),
    solidsCaptured: captured,
    power: e.power,
    solidsMass: 0,
    bowlFill: 0,
    effectiveRadius: e.effectiveRadius,
    reParticle: e.reParticle,
    timeToFill: captured > 0 ? p.solidsHoldup / captured : Infinity,
  };
  return { outputs, warnings, diagnostics: { converged: true, iterations: 0, residual: 0 } };
}
